
import React, { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { connect, useDispatch } from "react-redux";
import { motion } from "framer-motion";

import { AppDispatch, RootState } from "../../store";
import { indicadoresActions } from "../../redux/actions/Otros/indicadoresActions";
import { actualizarIndicadorHabilitadoActions } from "../../redux/actions/Otros/actualizarIndicadorHabilitadoActions";

export interface IndicadoresProps {
    valor: string | number;
}

interface Props {
    indicadoresActions: () => Promise<boolean>;
    habilitados: Record<IndicadorKey, boolean>;
    utm: IndicadoresProps;
    uf: IndicadoresProps;
    dolar: IndicadoresProps;
    ipc: IndicadoresProps;
    btc: IndicadoresProps;
    euro: IndicadoresProps;
    Ethereum: IndicadoresProps;
    isDarkMode: boolean;
}

type IndicadorKey = "UTM" | "UF" | "Dólar" | "IPC" | "BTC" | "EURO" | "Ethereum";

interface Indicador {
    id: IndicadorKey;
    title: string;
    rawValue: string | number;
    value: string;
    habilitado: boolean;
}

const Indicadores: React.FC<Props> = ({
    indicadoresActions,
    habilitados,
    utm,
    uf,
    dolar,
    ipc,
    btc,
    euro,
    Ethereum,
    isDarkMode,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState(false);

    // Convierte el valor original a número, admitiendo formatos chilenos.
    const obtenerNumero = (valor: string | number): number => {
        if (typeof valor === "number") {
            return valor;
        }

        const texto = String(valor ?? "").trim();

        if (texto === "") {
            return 0;
        }

        // Ejemplo: "1.234,56" -> 1234.56
        const normalizado = texto.includes(",")
            ? texto.replace(/\./g, "").replace(",", ".")
            : texto;

        const numero = Number(normalizado);

        return Number.isFinite(numero) ? numero : 0;
    };

    // Determina si un indicador tiene un valor disponible.
    const noDisponible = (valor: string | number): boolean => {
        const texto = String(valor ?? "").trim();

        if (texto === "") {
            return true;
        }

        return obtenerNumero(valor) === 0;
    };

    // Formatea los valores usando el formato numérico de Chile.
    const formatearValor = (
        valor: string | number,
        decimales = 0
    ): string => {
        const numero = obtenerNumero(valor);

        return numero.toLocaleString("es-CL", {
            minimumFractionDigits: decimales,
            maximumFractionDigits: decimales,
        });
    };

    // Los valores originales se mantienen separados de los valores mostrados.
    const indicadoresData: Indicador[] = [
        {
            id: "UTM",
            title: "UTM",
            rawValue: utm.valor,
            value: `$${formatearValor(utm.valor)}`,
            habilitado: habilitados.UTM,
        },
        {
            id: "UF",
            title: "UF",
            rawValue: uf.valor,
            value: `$${formatearValor(uf.valor, 2)}`,
            habilitado: habilitados.UF,
        },
        {
            id: "Dólar",
            title: "Dólar",
            rawValue: dolar.valor,
            value: `$${formatearValor(dolar.valor, 2)}`,
            habilitado: habilitados["Dólar"],
        },
        {
            id: "IPC",
            title: "IPC",
            rawValue: ipc.valor,
            value: `${formatearValor(ipc.valor, 2)}%`,
            habilitado: habilitados.IPC,
        },
        {
            id: "BTC",
            title: "BTC",
            rawValue: btc.valor,
            value: `$${formatearValor(btc.valor)}`,
            habilitado: habilitados.BTC,
        },
        {
            id: "EURO",
            title: "EURO",
            rawValue: euro.valor,
            value: `$${formatearValor(euro.valor, 2)}`,
            habilitado: habilitados.EURO,
        },
        {
            id: "Ethereum",
            title: "Ethereum",
            rawValue: Ethereum.valor,
            value: `$${formatearValor(Ethereum.valor, 2)}`,
            habilitado: habilitados.Ethereum,
        },
    ];

    // Actualiza el estado global del indicador seleccionado.
    const onToggle = (id: IndicadorKey) => {
        dispatch(
            actualizarIndicadorHabilitadoActions(id, !habilitados[id])
        );
    };

    // Carga los indicadores cuando los valores principales no están disponibles.
    const cargaIndicadores = useCallback(async () => {
        const sinValores =
            obtenerNumero(uf.valor) === 0 &&
            obtenerNumero(utm.valor) === 0 &&
            obtenerNumero(dolar.valor) === 0 &&
            obtenerNumero(ipc.valor) === 0;

        if (!sinValores) return;

        setLoading(true);

        try {
            await indicadoresActions();
        } catch (error) {
            console.error("Error al cargar los indicadores:", error);
        } finally {
            setLoading(false);
        }
    }, [
        indicadoresActions,
        uf.valor,
        utm.valor,
        dolar.valor,
        ipc.valor,
    ]);

    useEffect(() => {
        void cargaIndicadores();
    }, [cargaIndicadores]);

    return (
        <Row className="g-2 mb-5">
            <Col xs={12}>
                <div className="d-flex border-bottom align-items-center p-2">
                    <p className="mb-0">
                        Habilita los indicadores que deseas mostrar en el
                        panel izquierdo de la pantalla.
                    </p>
                </div>
            </Col>

            {indicadoresData.map((item) => {
                // IMPORTANTE: validar el valor original, no el texto formateado.
                const indicadorNoDisponible = noDisponible(item.rawValue);

                return (
                    <Col lg={6} md={6} sm={12} key={item.id}>
                        <div
                            className={`text-center p-2 shadow-sm rounded h-100 d-flex align-items-center justify-content-between ${isDarkMode
                                ? "bg-dark text-white"
                                : "bg-secondary text-white"
                                } ${indicadorNoDisponible ? "opacity-50" : ""
                                }`}
                        >
                            <div className="mb-1">
                                <strong className="form-label me-2">
                                    {item.title}:
                                </strong>

                                {indicadorNoDisponible
                                    ? "No disponible"
                                    : item.value}
                            </div>

                            <button
                                type="button"
                                disabled={indicadorNoDisponible}
                                onClick={() => {
                                    if (!indicadorNoDisponible) {
                                        onToggle(item.id);
                                    }
                                }}
                                aria-pressed={
                                    indicadorNoDisponible
                                        ? false
                                        : item.habilitado
                                }
                                aria-label={
                                    indicadorNoDisponible
                                        ? `${item.title} no disponible`
                                        : `${item.habilitado
                                            ? "Deshabilitar"
                                            : "Habilitar"
                                        } indicador ${item.title}`
                                }
                                title={
                                    indicadorNoDisponible
                                        ? "Indicador no disponible"
                                        : `${item.habilitado
                                            ? "Deshabilitar"
                                            : "Habilitar"
                                        } ${item.title}`
                                }
                                className={`button-moon-sun position-relative d-flex align-items-center justify-content-start rounded-pill border-0 p-0 ${indicadorNoDisponible
                                    ? "bg-secondary text-white"
                                    : item.habilitado
                                        ? "bg-success text-white"
                                        : "bg-secondary-subtle text-dark"
                                    }`}
                                style={{
                                    width: "110px",
                                    height: "28px",
                                    cursor: indicadorNoDisponible
                                        ? "not-allowed"
                                        : "pointer",
                                    overflow: "hidden",
                                }}
                            >
                                <span
                                    className="position-absolute w-100 text-center fw-semibold"
                                    style={{
                                        fontSize: "10px",
                                        pointerEvents: "none",
                                    }}
                                >
                                    {indicadorNoDisponible
                                        ? "No disponible"
                                        : item.habilitado
                                            ? "Activo"
                                            : "Inactivo"}
                                </span>

                                {!indicadorNoDisponible && (
                                    <motion.span
                                        className="position-absolute d-flex align-items-center justify-content-center rounded-circle bg-white text-dark shadow-sm"
                                        animate={{
                                            x: item.habilitado ? 80 : 3,
                                        }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 500,
                                            damping: 30,
                                        }}
                                        style={{
                                            width: "22px",
                                            height: "22px",
                                            top: "3px",
                                            left: 0,
                                            zIndex: 1,
                                            pointerEvents: "none",
                                        }}
                                        aria-hidden="true"
                                    />
                                )}
                            </button>
                        </div>
                    </Col>
                );
            })}

            {loading && (
                <Col xs={12}>
                    <div className="text-center mt-2">
                        Cargando indicadores...
                    </div>
                </Col>
            )}
        </Row>
    );
};

const mapStateToProps = (state: RootState) => ({
    utm: state.indicadoresReducers.utm || 0,
    uf: state.indicadoresReducers.uf || 0,
    dolar: state.indicadoresReducers.dolar || 0,
    ipc: state.indicadoresReducers.ipc || 0,
    btc: state.indicadoresReducers.btc || 0,
    euro: state.indicadoresReducers.euro || 0,
    Ethereum: state.indicadoresReducers.Ethereum || 0,
    isDarkMode: state.darkModeReducer.isDarkMode,
    habilitados: state.actualizarIndicadorHabilitadoReducers.habilitados || [],
});

export default connect(mapStateToProps, {
    indicadoresActions,
})(Indicadores);
